// routes/admin/AdminUserOverview.tsx
import { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "../../lib/supabaseClient";
import { useUserProfile } from "../../contexts/UserProfileContext";

type User = {
  id: string;
  first_name: string | null;
  last_name: string | null;
  email: string | null;
};

export default function AdminUserOverview() {
  const { profile } = useUserProfile();
  const navigate = useNavigate();
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");

  useEffect(() => {
    if (!profile || profile.role !== "admin") return;

    const loadUsers = async () => {
      const { data, error } = await supabase
        .from("profiles")
        .select("id, first_name, last_name, email")
        .order("first_name", { ascending: true })
        .order("last_name", { ascending: true });

      if (error) {
        console.error("❌ Fehler beim Laden der Nutzer:", error);
      } else {
        setUsers(data);
      }

      setLoading(false);
    };

    loadUsers();
  }, [profile]);

  const filteredUsers = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return users;
    return users.filter((u) => {
      const name = `${u.first_name ?? ""} ${u.last_name ?? ""}`.toLowerCase();
      const email = (u.email ?? "").toLowerCase();
      return name.includes(q) || email.includes(q);
    });
  }, [users, search]);

  if (!profile || profile.role !== "admin") {
    return (
      <div className="min-h-screen bg-[#0d1117] flex items-center justify-center">
        <p className="text-red-400">Kein Zugriff – nur für Admins 🛑</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0d1117]">
      <div className="max-w-6xl mx-auto pt-10 pb-20 px-4 sm:px-6">
        <h1 className="text-2xl font-bold mb-6 text-slate-100">
          👥 Nutzerübersicht
        </h1>

        <input
          type="search"
          placeholder="Suche nach Name oder E-Mail…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-full max-w-md mb-6 p-2 border border-[#2a3347] bg-[#0d1117] text-slate-100 rounded outline-none focus:border-sky-400 placeholder:text-slate-600"
        />

        {loading ? (
          <p className="text-slate-500">Lade Nutzer …</p>
        ) : filteredUsers.length === 0 ? (
          <p className="text-slate-500 text-sm">Keine Nutzer gefunden.</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredUsers.map((user) => (
              <button
                key={user.id}
                onClick={() => navigate(`/admin/users/${user.id}`)}
                className="border border-[#2a3347] p-4 rounded-xl bg-[#161b27] hover:border-sky-500/20 transition-colors text-left"
              >
                <div className="font-semibold text-slate-100">
                  {user.first_name || ""} {user.last_name || ""}
                </div>
                <div className="text-sm text-slate-400">{user.email}</div>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
